import type { URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { openAndWaitForCallback } from '@tbitop/platform';
import type Keycloak from 'keycloak-js';
import type { KeycloakAdapter, KeycloakLoginOptions, KeycloakLogoutOptions, KeycloakRegisterOptions } from 'keycloak-js';
import type { ExtendedKeycloakInstance, KeycloakPromise } from '../keycloak-js';
import { log } from '../log';

export class CapacitorAdapter implements KeycloakAdapter {

  private keycloak: ExtendedKeycloakInstance;

  constructor(
    keycloak: Keycloak,
  ) {
    log.trace('Instantiating Keycloak Capacitor adapter.', keycloak);
    this.keycloak = keycloak as ExtendedKeycloakInstance;
  }

  login(options?: KeycloakLoginOptions): Promise<void> {
    const loginUrl = this.keycloak.createLoginUrl(options);

    return this.openAndWaitForCallback(
      loginUrl,
      ({ url }, promise) => {
        log.trace('Login callback received.', url);
        this.keycloak.processCallback(
          this.keycloak.parseCallback(url),
          promise,
        );
      },
    );
  }

  logout(options?: KeycloakLogoutOptions): Promise<void> {
    const logoutUrl = this.keycloak.createLogoutUrl(options);

    return this.openAndWaitForCallback(
      logoutUrl,
      (_, promise) => {
        log.trace('Logout callback received.');
        this.keycloak.clearToken();
        // wait for in-app browser to be completely closed before continuing
        setTimeout(() => promise.promise.then(), 500);
      },
    );
  }

  register(options?: KeycloakRegisterOptions): Promise<void> {
    const registerUrl = this.keycloak.createRegisterUrl(options);

    return this.openAndWaitForCallback(
      registerUrl,
      (data, promise) => {
        this.keycloak.processCallback(
          this.keycloak.parseCallback(data.url),
          promise,
        );
      },
    );
  }

  accountManagement(): Promise<void> {
    const promise = this.keycloak.createPromise();
    const accountUrl = this.keycloak.createAccountUrl();

    if (typeof accountUrl !== 'undefined') {
      return promise.promise.catch(_ => log.error('Account management not supported by the OIDC server.'));
    }

    Browser.open({ url: accountUrl })
      .then(() => promise.promise.then());

    return promise.promise;
  }

  redirectUri(options: { redirectUri: string }, encodeHash = true): string {
    if (options?.redirectUri) {
      return options.redirectUri;
    } else if (this.keycloak.redirectUri) {
      return this.keycloak.redirectUri;
    } else {
      return 'http://localhost';
    }
  }

  private openAndWaitForCallback(url: string, callback: (event: URLOpenListenerEvent, promise: KeycloakPromise<void, void>) => void) {
    const promise = this.keycloak.createPromise();

    openAndWaitForCallback(url)
      .then(event => callback(event, promise));

    return promise.promise;
  }
}
